import clsx from 'clsx'
import moment from 'moment'
import {FC, useEffect, useMemo, useState} from 'react'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import {KTSVG} from '../../../../_gori/helpers'
import {PageTitle} from '../../../../_gori/layout/core'
import {BranchesMenuModal} from '../modals/BranchesMenuModal'
import {OrganizationAccounts} from './OrganizationAccounts'
import {OrganizationOverview} from './OrganizationOverview'
import {useOrganizationBranches} from '../providers/OrganizationBranchesProvider'
import {getRangeForPreset, TimeRange, TIME_RANGES} from '../core/_const'

const OrganizationsPage: FC = () => {
  const intl = useIntl()
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts'>('overview')
  const [timeRange, setTimeRange] = useState<TimeRange>('ytd')
  const [isCustomRange, setIsCustomRange] = useState(false)
  const [dateRange, setDateRange] = useState(() => getRangeForPreset('ytd'))
  const [pickerKey, setPickerKey] = useState(0)
  const [showBranchesMenuModal, setShowBranchesMenuModal] = useState(false)
  const {branches, appliedAccountIds, applyBranchesFromModal} = useOrganizationBranches()
  const analyticsFilters = useMemo(
    () => ({
      accountIds: appliedAccountIds,
      timeRange,
      isCustomRange,
      dateRange,
    }),
    [appliedAccountIds, dateRange, isCustomRange, timeRange]
  )

  const handleSelectTimeRange = (range: TimeRange) => {
    setIsCustomRange(false)
    setTimeRange(range)
    setDateRange(getRangeForPreset(range))
  }

  // Force remount DateRangePicker when locale changes
  useEffect(() => {
    setPickerKey((prev) => prev + 1)
  }, [intl])

  const dateRangePickerLocale = useMemo(
    () => ({
      customRangeLabel: intl.formatMessage({id: 'CUSTOM_RANGE'}),
      cancelLabel: intl.formatMessage({id: 'CANCEL'}),
      applyLabel: intl.formatMessage({id: 'APPLY'}),
      fromLabel: intl.formatMessage({id: 'FROM'}),
      toLabel: intl.formatMessage({id: 'TO'}),
      daysOfWeek: [
        intl.formatMessage({id: 'SU'}),
        intl.formatMessage({id: 'MO'}),
        intl.formatMessage({id: 'TU'}),
        intl.formatMessage({id: 'WE'}),
        intl.formatMessage({id: 'TH'}),
        intl.formatMessage({id: 'FR'}),
        intl.formatMessage({id: 'SA'}),
      ],
      monthNames: [
        intl.formatMessage({id: 'JANUARY'}),
        intl.formatMessage({id: 'FEBRUARY'}),
        intl.formatMessage({id: 'MARCH'}),
        intl.formatMessage({id: 'APRIL'}),
        intl.formatMessage({id: 'MAY'}),
        intl.formatMessage({id: 'JUNE'}),
        intl.formatMessage({id: 'JULY'}),
        intl.formatMessage({id: 'AUGUST'}),
        intl.formatMessage({id: 'SEPTEMBER'}),
        intl.formatMessage({id: 'OCTOBER'}),
        intl.formatMessage({id: 'NOVEMBER'}),
        intl.formatMessage({id: 'DECEMBER'}),
      ],
    }),
    [intl]
  )

  const handleApplyCustomDateRange = (
    _event: unknown,
    picker: {startDate: moment.Moment; endDate: moment.Moment}
  ) => {
    setIsCustomRange(true)
    setDateRange({start: moment(picker.startDate), end: moment(picker.endDate)})
  }

  return (
    <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
      <div className='d-flex flex-column h-100 organizations-page'>
        <PageTitle
          trailingActions={
            <button
              type='button'
              className='organizations-page__account-filter-btn'
              onClick={() => setShowBranchesMenuModal(true)}
            >
              {intl.formatMessage({id: 'ADJUST_ACCOUNT_FILTER'})}
            </button>
          }
        >
          {intl.formatMessage({id: 'ORGANIZATIONS'})}
        </PageTitle>

        <BranchesMenuModal
          show={showBranchesMenuModal}
          handleClose={() => setShowBranchesMenuModal(false)}
          initialBranches={branches}
          onApply={(nextBranches) => applyBranchesFromModal(nextBranches)}
        />

        <div className='organizations-page__account-filter-btn-container pb-5 justify-content-end'>
          <button
            type='button'
            className='organizations-page__account-filter-btn'
            onClick={() => setShowBranchesMenuModal(true)}
          >
            {intl.formatMessage({id: 'ADJUST_ACCOUNT_FILTER'})}
          </button>
        </div>
        <div className='d-flex justify-content-between align-items-start flex-column-reverse flex-md-row'>
          <div className='table-tabs'>
            <div
              className={clsx('table-tabs__tab', {active: activeTab === 'overview'})}
              onClick={() => setActiveTab('overview')}
            >
              <div>{intl.formatMessage({id: 'OVERVIEW'})}</div>
            </div>
            <div
              className={clsx('table-tabs__tab', {active: activeTab === 'accounts'})}
              onClick={() => setActiveTab('accounts')}
            >
              <div>{intl.formatMessage({id: 'ACCOUNTS'})}</div>
            </div>
          </div>
          {activeTab === 'overview' && (
            <div className='organizations-time-range'>
              <div
                className='organizations-time-range__segment'
                role='group'
                aria-label='Time range'
              >
                {TIME_RANGES.map((range) => (
                  <span
                    key={range.id}
                    className={clsx('organizations-time-range__item align-items-center', {
                      'organizations-time-range__item--active':
                        !isCustomRange && timeRange === range.id,
                    })}
                    onClick={() => handleSelectTimeRange(range.id)}
                  >
                    {range.label}
                  </span>
                ))}
              </div>
              <DateRangePicker
                key={pickerKey}
                initialSettings={{
                  locale: dateRangePickerLocale,
                  startDate: dateRange.start,
                  endDate: dateRange.end,
                  opens: 'left',
                  ranges: false,
                  alwaysShowCalendars: true,
                  maxDate: moment(),
                }}
                onApply={handleApplyCustomDateRange}
              >
                <button
                  type='button'
                  className={clsx('organizations-time-range__calendar-btn', {
                    'organizations-time-range__calendar-btn--active': isCustomRange,
                  })}
                  aria-label={intl.formatMessage({id: 'CUSTOM_RANGE'})}
                >
                  <KTSVG path='media/gori/chatbot/calendar.svg' svgClassName='' small={false} />
                </button>
              </DateRangePicker>
            </div>
          )}
        </div>

        <div className='rounded-start-0 d-flex flex-column flex-column-fluid'>
          {activeTab === 'overview' ? (
            <OrganizationOverview {...analyticsFilters} />
          ) : (
            <OrganizationAccounts />
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export {OrganizationsPage}
